import type { CoverageRow, CoverageSort } from '@fruuit/shared'
import { Badge, type Column, DataTable, StatusBadge, statusLabel } from '@fruuit/ui'
import { ArrowRight } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { countryLabel, coverageLabelTone, formatDate, sectorLabel, themeLabel } from './format'

export interface CoverageTableProps {
  rows: CoverageRow[]
  loading: boolean
  /** Where company and case links point (`/app` or `/workspace`). */
  base: '/app' | '/workspace'
  /** Staff columns (security, last search, open exception). */
  staff?: boolean
  /** Query string appended to links in the workspace (`?org=…`). */
  linkQuery?: string
  sort: CoverageSort
  dir: 'asc' | 'desc'
  onSortChange: (sort: CoverageSort, dir: 'asc' | 'desc') => void
  pagination?: {
    hasNext: boolean
    hasPrevious: boolean
    onNext: () => void
    onPrevious: () => void
    summary?: string
  }
  empty: ReactNode
  onResolve?: (row: CoverageRow) => void
}

const SORTABLE: Record<string, CoverageSort> = {
  company: 'company',
  portfolio: 'portfolio',
  finding: 'finding',
  review: 'next_review',
}

/** The coverage register (S2, PORT-001, PORT-005): one row per latest position. */
export function CoverageTable({
  rows,
  loading,
  base,
  staff = false,
  linkQuery = '',
  sort,
  dir,
  onSortChange,
  pagination,
  empty,
  onResolve,
}: CoverageTableProps) {
  const columns: Column<CoverageRow>[] = [
    {
      id: 'company',
      header: 'Company / security',
      sortable: true,
      cell: (r) => (
        <div className="min-w-[13rem]">
          {r.issuer_id ? (
            <Link
              to={`${base}/companies/${r.issuer_id}${linkQuery}`}
              className="font-semibold text-teal hover:underline"
            >
              {r.company}
            </Link>
          ) : (
            <span className="font-semibold text-ink">{r.company}</span>
          )}
          <span className="mt-0.5 block text-xs text-sage">
            {r.identifier_value}
            {' · '}
            {r.issuer_id
              ? [sectorLabel(r.sector), countryLabel(r.country)].filter(Boolean).join(' · ')
              : r.issuer_name_hint
                ? `Client name: ${r.issuer_name_hint}`
                : 'Mapping needs review'}
          </span>
        </div>
      ),
    },
    {
      id: 'portfolio',
      header: 'Portfolio',
      sortable: true,
      cell: (r) => (
        <div className="min-w-[9rem]">
          <span className="text-ink">{r.portfolio}</span>
          <span className="mt-0.5 block text-xs text-sage">Position {formatDate(r.as_of)}</span>
        </div>
      ),
    },
    {
      id: 'finding',
      header: 'Research finding',
      sortable: true,
      cell: (r) => (
        <div className="min-w-[12rem]">
          <StatusBadge status={r.finding} label={r.finding} />
          <span className="mt-1 block text-xs text-sage">
            {r.assessment_version
              ? `Published version ${r.assessment_version}`
              : 'No released assessment'}
          </span>
        </div>
      ),
    },
    {
      id: 'engagement',
      header: 'Engagement',
      cell: (r) =>
        r.engagements.length > 0 ? (
          <ul className="min-w-[9rem] space-y-0.5">
            {r.engagements.map((e) => (
              <li key={e.id}>
                <Link
                  to={`${base}/cases/${e.id}${linkQuery}`}
                  className="text-sm text-teal hover:underline"
                >
                  {themeLabel(e.theme)} · {statusLabel(e.status).toLowerCase()}
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <span className="text-sm text-sage">Not engaged</span>
        ),
    },
    {
      id: 'review',
      header: 'Review status',
      sortable: true,
      cell: (r) => (
        <div className="min-w-[11rem]">
          <Badge tone={coverageLabelTone(r.coverage_label)} dot>
            {r.coverage_label}
          </Badge>
          <span className="mt-1 block text-xs text-sage">
            Next review {formatDate(r.next_review, 'not scheduled')}
          </span>
        </div>
      ),
    },
  ]
  if (staff) {
    columns.push({
      id: 'staff',
      header: 'Team detail',
      cell: (r) => (
        <div className="min-w-[10rem] text-xs leading-relaxed text-sage">
          <span className="block">
            Last search:{' '}
            {r.staff?.search_run_state
              ? `${statusLabel(r.staff.search_run_state)} · ${formatDate(r.staff.last_search_date)}`
              : 'none recorded'}
          </span>
          {r.staff?.pending_assessment_state ? (
            <span className="block">
              Newer version: {statusLabel(r.staff.pending_assessment_state)}
            </span>
          ) : null}
          {r.staff?.open_exception_id && onResolve ? (
            <button
              type="button"
              onClick={() => onResolve(r)}
              className="mt-1 font-semibold text-teal hover:underline"
            >
              Resolve mapping
            </button>
          ) : null}
        </div>
      ),
    })
  }
  columns.push({
    id: 'open',
    header: <span className="sr-only">Open</span>,
    align: 'right',
    cell: (r) =>
      r.issuer_id ? (
        <Link
          to={`${base}/companies/${r.issuer_id}${linkQuery}`}
          aria-label={`Open ${r.company}`}
          className="inline-flex size-8 items-center justify-center rounded-md text-teal hover:bg-wash"
        >
          <ArrowRight aria-hidden="true" className="size-4" />
        </Link>
      ) : (
        <StatusBadge status="unresolved" label="Unresolved" />
      ),
  })

  const columnFor = Object.entries(SORTABLE).find(([, value]) => value === sort)?.[0] ?? 'company'
  return (
    <DataTable
      caption="Portfolio coverage"
      columns={columns}
      rows={rows}
      getRowId={(r) => r.holding_id}
      loading={loading}
      manualSort
      sort={{ columnId: columnFor, direction: dir }}
      onSortChange={(next) => onSortChange(SORTABLE[next.columnId] ?? 'company', next.direction)}
      empty={empty}
      {...(pagination ? { pagination: { mode: 'server' as const, ...pagination } } : {})}
    />
  )
}
